import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MultiSelect } from "@/components/ui/multi-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, AlertTriangle, CheckCircle, Clock, Wifi, WifiOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as tenantsApi from "@/services/tenantsApi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import * as tagsApi from "@/services/tagsApi";
import * as tagAssignmentApi from "@/services/tagAssignmentApi";
import { mapApiTagAssignment } from "@/utils/tagMapping";
import { getCurrentUserEmail } from "@/lib/auth";
import type {
  MultipleAppTag,
  MultipleDeviceTag,
  MultiplePolicyTag,
  AppTag,
  DeviceTagAssignment,
  PolicyTag,
} from "@/types/api";

import { MappedTagAssignment } from "@/utils/tagMapping";
type Tag = MappedTagAssignment;

interface TagAssignmentDialogProps {
  open: boolean; onOpenChange: (open: boolean) => void;
  resourceId?: string; resourceIds?: string[];
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user";
  resourceName?: string; onAssignmentComplete?: () => void; onComplete?: () => void;
}

export const TagAssignmentDialog = ({
  open, onOpenChange, resourceId, resourceIds, resourceType,
  resourceName = "", onAssignmentComplete, onComplete
}: TagAssignmentDialogProps) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [assignedTags, setAssignedTags] = useState<Tag[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [estimatedDeviceCount, setEstimatedDeviceCount] = useState(0);
  const [isAssigning, setIsAssigning] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [tagToRemove, setTagToRemove] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();
  const [tenantsList, setTenantsList] = useState<{id: string; name: string}[]>([]);

  useEffect(() => {
    if (!open) return;
    tenantsApi.listTenants().then((tenants: any[]) => {
      setTenantsList((tenants || []).map((t: any) => ({
        id: t.id || t.tenantId || "",
        name: t.name || t.tenantName || t.companyName || t.id || "Unknown",
      })).filter((c: any) => c.id));
    }).catch(() => setTenantsList([]));
  }, [open]);

  const customerOptions = tenantsList.map((t) => ({ value: t.id, label: t.name }));

  useEffect(() => {
    if (open && selectedCustomers.length > 0) fetchTagsAndAssignments();
  }, [open, resourceId, resourceIds, selectedCustomers]);

  const fetchTagsAndAssignments = async () => {
    setIsLoading(true); setConnectionError(false); setFallbackMessage(null);
    try {
      // Fetch sequentially to avoid localStorage tenant ID race condition
      const allApiTags: any[] = [];
      for (const cid of selectedCustomers) {
        const tags = await tagsApi.listTagsByTenant(cid);
        // Filter client-side: only keep tags whose tenantId matches this customer
        const filtered = (tags || []).filter((t: any) => {
          const tagTenantId = t.tenantId || t.tenant_id;
          return !tagTenantId || tagTenantId === cid;
        });
        allApiTags.push(...filtered);
      }
      const mapped: Tag[] = allApiTags.map((t: any) => mapApiTagAssignment(t));
      setAvailableTags(mapped);

      if (resourceId && !resourceIds) {
        try {
          let assigned: any[] = [];
          if (resourceType === "device") assigned = await tagsApi.getTagsByDevice(resourceId);
          else if (resourceType === "application") assigned = await tagsApi.getTagsByApp(resourceId);
          else if (resourceType === "profile") assigned = await tagsApi.getTagsByPolicy(resourceId);
          setAssignedTags(assigned.map((t: any) => mapApiTagAssignment(t)));
        } catch { setAssignedTags([]); }
      } else { setAssignedTags([]); }
    } catch (error: any) {
      console.error("[TagAssignment] Failed to fetch tags:", error);
      setConnectionError(true);
      toast({ title: "Connection Error", description: "Failed to fetch tags. Please try again.", variant: "destructive" });
    } finally { setIsLoading(false); }
  };

  const handleTagSelection = (tagId: string, checked: boolean) => {
    if (checked) setSelectedTagIds([...selectedTagIds, tagId]);
    else setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
  };

  const handleAssignTags = () => {
    if (selectedTagIds.length === 0) { toast({ title: "No tags selected", description: "Please select at least one tag.", variant: "destructive" }); return; }
    setEstimatedDeviceCount(Math.floor(Math.random() * 50) + 10);
    setShowConfirmation(true);
  };

  const confirmAssignment = async () => {
    setIsAssigning(true); setIsLoading(true);
    try {
      const targetIds = resourceIds?.length ? resourceIds : [resourceId!];
      const userEmail = getCurrentUserEmail() || "system";
      const base = { tag_ids: selectedTagIds, created_by: userEmail, updated_by: userEmail };

      if (resourceType === "device") {
        const payload: MultipleDeviceTag = { ...base, device_ids: targetIds };
        await tagAssignmentApi.assignDeviceTags(payload);
      } else if (resourceType === "application") {
        const payload: MultipleAppTag = { ...base, app_ids: targetIds };
        await tagAssignmentApi.assignAppTags(payload);
      } else if (resourceType === "profile") {
        const payload: MultiplePolicyTag = { ...base, policy_ids: targetIds };
        await tagAssignmentApi.assignPolicyTags(payload);
      }

      const newlyAssigned = availableTags.filter(tag => selectedTagIds.includes(tag.id));
      setAssignedTags([...assignedTags, ...newlyAssigned]);
      setSelectedTagIds([]); setShowConfirmation(false);
      toast({ title: "✅ Tags Assigned Successfully", description: `${selectedTagIds.length} tag(s) assigned to ${targetIds.length} ${resourceType}(s).` });
      onAssignmentComplete?.(); onComplete?.();
    } catch (error: any) {
      console.error("[TagAssignment] Assignment failed:", error);
      setShowConfirmation(false);
      toast({ title: "❌ Assignment Failed", description: error.message || "Failed to assign tags.", variant: "destructive" });
    } finally { setIsAssigning(false); setIsLoading(false); }
  };

  const initiateRemoveTag = (tagId: string) => {
    const tag = assignedTags.find(t => t.id === tagId);
    if (tag) setTagToRemove({ id: tagId, name: tag.name });
  };

  const confirmRemoveTag = async () => {
    if (!tagToRemove) return;
    try {
      const targetIds = resourceIds?.length ? resourceIds : [resourceId!];
      const userEmail = getCurrentUserEmail() || "system";
      const base = { tag_ids: [tagToRemove.id], created_by: userEmail, updated_by: userEmail };

      if (resourceType === "device") {
        const payload: DeviceTagAssignment = { ...base, device_id: targetIds[0] };
        await tagAssignmentApi.unassignDeviceTags(payload);
      } else if (resourceType === "application") {
        const payload: AppTag = { ...base, app_id: targetIds[0] };
        await tagAssignmentApi.unassignAppTags(payload);
      } else if (resourceType === "profile") {
        const payload: PolicyTag = { ...base, policy_id: targetIds[0] };
        await tagAssignmentApi.unassignPolicyTags(payload);
      }
      setAssignedTags(assignedTags.filter(t => t.id !== tagToRemove.id));
      toast({ title: "Tag Removed", description: `"${tagToRemove.name}" has been removed.` });
    } catch (error: any) {
      toast({ title: "Removal Failed", description: "Failed to remove tag.", variant: "destructive" });
    } finally { setTagToRemove(null); }
  };

  const handleClose = () => { onOpenChange(false); onAssignmentComplete?.(); onComplete?.(); };

  const filteredTags = availableTags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCustomers = selectedCustomers.length === 0 || selectedCustomers.some(cid => tag.customers?.includes(cid));
    return matchesSearch && matchesCustomers;
  });
  const isTagAssigned = (tagId: string) => assignedTags.some(a => a.id === tagId);

  return (
    <>
      <Dialog open={open && !showConfirmation} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Tags to {resourceIds && resourceIds.length > 1 ? `${resourceIds.length} ${resourceType}s` : resourceName}</DialogTitle>
            <DialogDescription>Select tags to assign to this {resourceType}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {connectionError && <Alert variant="destructive"><WifiOff className="h-4 w-4" /><AlertDescription>Connection issues detected.</AlertDescription></Alert>}
            {fallbackMessage && <Alert variant={fallbackMessage.includes('Error') ? "destructive" : "default"}><CheckCircle className="h-4 w-4" /><AlertDescription>{fallbackMessage}</AlertDescription></Alert>}

            {(resourceId || (resourceIds && resourceIds.length > 0)) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Currently Assigned Tags</Label>
                <div className="border rounded-md p-3 bg-muted/30 min-h-[60px]">
                  {assignedTags.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">No tags assigned yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {assignedTags.map(tag => (
                        <Badge key={tag.id} variant="secondary" className="flex items-center gap-2 pl-2 pr-1 py-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                          <span className="text-sm">{tag.name}</span>
                          <Button variant="ghost" size="icon" className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10" onClick={() => initiateRemoveTag(tag.id)}><X className="h-3 w-3" /></Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-sm font-medium">Filter by Customer</Label>
              <MultiSelect
                options={customerOptions}
                value={selectedCustomers}
                onChange={setSelectedCustomers}
                placeholder="Select customers to filter tags"
                emptyMessage="No customers available"
              />
            </div>

            {selectedCustomers.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Available Tags</Label>
                <div className="relative mt-2"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input placeholder="Search tags..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8" /></div>
              </div>
            )}

            <ScrollArea className="h-64 border rounded-md p-2">
              <div className="space-y-2">
                {selectedCustomers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><p>Please select one or more customers to view their available tags.</p></div>
                ) : isLoading ? (
                  <div className="flex flex-col items-center justify-center py-8 space-y-2"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div><p className="text-sm text-muted-foreground">Loading tags...</p></div>
                ) : filteredTags.length > 0 ? (
                  filteredTags.map(tag => {
                    const isAssigned = isTagAssigned(tag.id);
                    const isSelected = selectedTagIds.includes(tag.id);
                    return (
                      <div key={tag.id} className={`flex items-center justify-between p-3 rounded-md border ${isAssigned ? 'bg-muted/30 border-muted' : 'hover:bg-muted/50 cursor-pointer'}`}>
                        <div className="flex items-center gap-3 flex-1">
                          {!isAssigned && <Checkbox checked={isSelected} onCheckedChange={(c) => handleTagSelection(tag.id, c as boolean)} />}
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                          <div className="flex-1"><div className="font-medium text-sm">{tag.name}</div>{tag.description && <div className="text-xs text-muted-foreground">{tag.description}</div>}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAssigned && <Badge variant="outline" className="text-xs bg-success-muted text-success-muted-foreground border-success-border">Already Assigned</Badge>}
                          <Badge variant={tag.type === "dynamic" ? "default" : "secondary"} className="text-xs">{tag.type}</Badge>
                        </div>
                      </div>
                    );
                  })
                ) : <div className="text-center py-4 text-muted-foreground">No tags match your filters</div>}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleAssignTags} disabled={selectedTagIds.length === 0 || selectedCustomers.length === 0 || isLoading || connectionError}>
              {isLoading ? <div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Loading...</div> : `Assign ${selectedTagIds.length > 0 ? `(${selectedTagIds.length})` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-warning" />Confirm Deployment</DialogTitle><DialogDescription>Review the tags before confirming.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">This will assign {selectedTagIds.length} tag(s) to <strong>{resourceName}</strong>.</p>
            <div className="space-y-2"><Label className="text-sm font-medium">Selected tags:</Label><div className="flex flex-wrap gap-1">{selectedTagIds.map(id => { const t = availableTags.find(x => x.id === id); return t ? <Badge key={id} variant="secondary" className="text-xs">{t.name}</Badge> : null; })}</div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmation(false)}>Cancel</Button>
            <Button onClick={confirmAssignment} disabled={isLoading || isAssigning}>
              {isAssigning ? <div className="flex items-center gap-2"><Clock className="h-4 w-4 animate-pulse" />Deploying...</div> : "Confirm & Deploy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!tagToRemove} onOpenChange={(open) => !open && setTagToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove Tag?</AlertDialogTitle><AlertDialogDescription>Remove "{tagToRemove?.name}" from this {resourceType}?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmRemoveTag} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
